import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Tabs } from 'antd';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ClientHeader from '../components/ClientHeader';
import QuickView from '../components/QuickView';
import Collections from '../components/Collections';
import Rolex from '../components/Rolex';
import Activity from '../components/Activity';
import { getInquiriesByClient } from '../../../../api/vendors';
import AllClientReminders from '../components/QuickView/Reminders/AllClientReminders';
import AllClientPaymentRequests from '../components/QuickView/PaymentRequest/AllClientPaymentRequests';
import AllClientSalesOpportunities from '../components/QuickView/SalesOpportunities/AllClientSalesOpportunities';
import getClientPaymentStatus from '../../../Layout/GetClientPaymentStatus';
import { getClientChat, setMsgClient, setMsgPrefill, resetAutoReminderId } from '../../../../reducers/messaging';
import { getRequest } from '../../../../core/apiRequests';
import { useOnce } from '../../../../hooks/useOnce';
import OpportunityDetails from '../components/QuickView/SalesOpportunities/components/OpportunityDetails';
import './styles.scss';
import useSearchParams from '../../../../hooks/useSearchParams';
import { setMergeClientDetailsFromAPI, setRecentActivity } from '../../../../reducers/client';
import ViewAllAppointments from '../../../../componentsV2/Routes/Clients/QuickView/Appointments/ViewAllAppointments';
import ViewAllAutoMessages from '../../../../componentsV2/Routes/Clients/QuickView/AutoMessages/ViewAllAutoMessages';

const SelectViewAll = ({
  allType,
  handleViewAll,
  allAssoc,
  client,
  auth,
  isFetchingAutoMessages,
  refetchAutoMessages,
  setSelectedSalesOpp,
  setDismissPopup,
  dismissPopup,
  associate,
  setAssociate,
  showPopConfirm,
  closeChat,
  showMessageHandler,
  selectedSalesOpp,
}) => {
  switch (allType) {
    case 'allAppointments':
      return <ViewAllAppointments setViewAllType={handleViewAll} allAssoc={allAssoc} />;
    case 'allAutoMessages':
      return (
        <ViewAllAutoMessages
          setViewAllType={handleViewAll}
          client={client}
          autoMessages={client?.autoMessages}
          client_name={client?.preferred_name || client?.name}
          associate_name={`${auth?.firstName || ''} ${auth?.lastName || ''}`}
          isFetching={isFetchingAutoMessages}
          refetch={refetchAutoMessages}
        />
      );
    case 'allPaymentRequests':
      return <AllClientPaymentRequests setViewAllType={handleViewAll} />;
    case 'allSalesOpportunities':
      return (
        <AllClientSalesOpportunities
          setViewAllType={handleViewAll}
          client={client}
          auth={auth}
          setSelectedSalesOpp={setSelectedSalesOpp}
        />
      );
    case 'allReminders':
      return (
        <AllClientReminders
          setViewAllType={handleViewAll}
          client={client}
          auth={auth}
          setDismissPopup={setDismissPopup}
          dismissPopup={dismissPopup}
          allAssoc={allAssoc}
          associate={associate}
          setAssociate={setAssociate}
          showPopConfirm={showPopConfirm}
          closeChat={closeChat}
          showMessageHandler={showMessageHandler}
        />
      );
    case 'salesOpportunityCardFromQV':
      return (
        <OpportunityDetails
          selectedSalesOpp={selectedSalesOpp}
          setViewAllType={handleViewAll}
          client={client}
          auth={auth}
        />
      );
    default:
      return null;
  }
};

const ClientsV2Details = ({
  setAssignPopup,
  setDrawerToggle,
  toggleDrawer,
  chatIconColor,
  type,
  client,
  auth,
  paymentBanner,
  activityList,
  associateRefObj,
  getClientData,
}) => {
  const [viewAll, setViewAll] = useState(false);
  const [viewAllType, setViewAllType] = useState('');
  const [dismissPopup, setDismissPopup] = useState(false);
  const [showPopConfirm, setShowPopConfirm] = useState(false);
  const [associate, setAssociate] = useState(0);
  const [reviewLink, setReviewLink] = useState('');
  const [selectedSalesOpp, setSelectedSalesOpp] = useState({});
  const flags = useFlags();
  const [hasActiveInquiry, setHasActiveInquiry] = useState(false);
  const { setValue, getValue: getSearchParam } = useSearchParams();
  const clientTabParams = getSearchParam('clientTab');
  const lastActivityRefresh = useRef(0);

  const clientId = client?.client_id || client?.id;

  useEffect(() => {
    if (flags.rolex && clientId) {
      getInquiriesByClient(clientId)
        .then(response => setHasActiveInquiry(!!response?.hasActiveInquiry))
        .catch(() => setHasActiveInquiry(false));
    }
  }, [flags.rolex, clientId]);

  const handleViewAll = useCallback(value => {
    if (value && value !== '') {
      setValue('viewAllTab', value);
    } else {
      setValue('viewAllTab', '');
    }
  }, []);

  const showMessageHandler = async () => {
    getClientPaymentStatus({ client, auth });
    await getClientChat(client);
    setShowPopConfirm(true);
  };

  const handleShareProducts = useCallback(
    async products => {
      const clientName = client?.preferred_name || client?.name?.split(' ')[0] || '';
      const productLines = products.map(p => `• ${p.collectionName || ''} (${p.mpn || ''})`).join('\n');
      const message = `Hi ${clientName}! I wanted to share these Rolex models with you:\n\n${productLines}\n\nWould you like to come in and take a look?`;
      setMsgPrefill(message);
      await showMessageHandler();
    },
    [client],
  );

  function closeChat() {
    setShowPopConfirm(false);
    // resetMessaging(); I removed this because at the safari we are getting an 404 page error
    resetAutoReminderId();
  }

  const { data: allAssoc } = useQuery(
    'associates-list',
    async () => {
      const reqObj = {
        params: ['associates'],
        loadingStatusOff: true,
      };
      setAssociate(auth.userId);
      const data = await getRequest(reqObj);
      const sortedAscts = data?.sort((a, b) => {
        if (`${a.first_name || ''}${a.last_name || ''}` > `${b.first_name || ''}${b.last_name}`) {
          return 1;
        }
        if (`${a.first_name || ''}${a.last_name || ''}` < `${b.first_name || ''}${b.last_name}`) {
          return -1;
        }
        return 0;
      });
      return sortedAscts;
    },
    {
      enabled: !!auth.merchantId,
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const getReviewLink = useCallback(async () => {
    if (auth.selectedStore) {
      const reqObj = {
        params: ['chats', auth.selectedStore, 'getReviewLink'],
      };
      const linkData = await getRequest(reqObj);
      if (linkData.length > 0) {
        const { review_link } = linkData[0];
        setReviewLink(review_link);
      }
    } else {
      console.error('Unable to getReviewLink, NO store ID!');
    }
  }, [auth.selectedStore]);

  const { call } = useOnce(getReviewLink);

  useEffect(() => {
    call();
  }, [auth.selectedStore]);

  const { refetch: refetchReminders } = useQuery(
    ['getReminders', client.client_id, auth.merchantId],
    async () => {
      if (!client.client_id || !auth.merchantId) return [];
      const reqObj = {
        params: ['clients', auth.merchantId, client.client_id, 'getClientReminders'],
        query: {
          limit: 4,
        },
        loadingStatusOff: true,
      };
      const data = await getRequest(reqObj);
      if (data) {
        setMergeClientDetailsFromAPI({ reminders: data?.reminders });
        return data.reminders;
      }
      return [];
    },
    {
      enabled: !!client.client_id && !!auth.merchantId,
      initialData: [],
      refetchOnWindowFocus: false,
    },
  );

  const { refetch: refetchAutoMessages, isFetching: isFetchingAutoMessages } = useQuery(
    ['getAutoMessages', client.client_id, auth.merchantId],
    async () => {
      const reqObj = {
        params: ['clients', auth.merchantId, client.client_id, 'getClientAutoMessages'],
        query: { limit: 5 },
      };
      const data = await getRequest(reqObj);
      if (data) {
        setMergeClientDetailsFromAPI({ autoMessages: data?.autoMessages });
        return data.autoMessages;
      }
      return [];
    },
    {
      enabled: !!client.client_id && !!auth.merchantId,
      initialData: [],
      refetchOnWindowFocus: false,
    },
  );

  const tabList = [
    {
      key: 'quick-view',
      label: (
        <span className="fw-500 fs-14 d-inline-block" style={{ padding: '8px 0px' }}>
          Quick View
        </span>
      ),
      children: (
        <QuickView
          setViewAllType={handleViewAll}
          client={client}
          auth={auth}
          showPopConfirm={showPopConfirm}
          closeChat={closeChat}
          showMessageHandler={showMessageHandler}
          setDismissPopup={setDismissPopup}
          dismissPopup={dismissPopup}
          allAssoc={allAssoc}
          associate={associate}
          setAssociate={setAssociate}
          reviewLink={reviewLink}
          setSelectedSalesOpp={setSelectedSalesOpp}
          getClientData={getClientData}
          refetchReminders={refetchReminders}
          refetchAutoMessages={refetchAutoMessages}
          merchantId={auth.merchantId}
        />
      ),
    },
    ...(flags.rolex
      ? [
          {
            key: 'rolex',
            label: (
              <span className="fw-500 fs-14 d-inline-block" style={{ padding: '8px 0px', position: 'relative' }}>
                Rolex
                {hasActiveInquiry && (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: '#33AAFC',
                      position: 'absolute',
                      top: 4,
                      right: -8,
                      display: 'inline-block',
                    }}
                  />
                )}
              </span>
            ),
            children: <Rolex client={client} auth={auth} onShareProducts={handleShareProducts} />,
          },
        ]
      : []),
    {
      key: 'activity',
      label: (
        <span className="fw-500 fs-14 d-inline-block" style={{ padding: '8px 0px', cursor: 'pointer' }}>
          Activity
        </span>
      ),
      children: (
        <Activity
          setViewAllType={handleViewAll}
          associateRefObj={associateRefObj}
          client={client}
          activityList={activityList}
          auth={auth}
          refetchReminders={refetchReminders}
          refetchAutoMessages={refetchAutoMessages}
        />
      ),
    },
    {
      key: 'collections',
      label: (
        <span className="fw-500 fs-14 d-inline-block" style={{ padding: '8px 0px' }}>
          Collections
        </span>
      ),
      children: <Collections setViewAllType={handleViewAll} client={client} auth={auth} />,
    },
  ];

  const disabledAssociate = useMemo(() => {
    return true;
  }, [viewAllType, selectedSalesOpp]);
  // set default tab
  useEffect(() => {
    const isCollectionId = getSearchParam('collectionId');
    const activityFilter = getSearchParam('activityFilter');
    const oppId = getSearchParam('oppId');
    const oppLocation = getSearchParam('oppLocation');
    if (isCollectionId) {
      setValue('clientTab', 'collections');
    } else if (activityFilter) {
      setValue('clientTab', 'activity');
    } else if (oppId) {
      setValue('clientTab', 'quick-view');
      setSelectedSalesOpp({ id: oppId, oppLocation: oppLocation || 'quickViewSalesOpp' });
      setViewAll(true);
      setViewAllType('salesOpportunityCardFromQV');
    } else if (getSearchParam('clientTab') === 'rolex' && !flags.rolex) {
      setValue('clientTab', 'quick-view');
    } else if (!getSearchParam('clientTab')) {
      setValue('clientTab', 'quick-view');
      setValue('collectionId', '');
      setValue('activityFilter', '');
    }
  }, []);

  const oppId = getSearchParam('oppId');
  const oppLocation = getSearchParam('oppLocation');
  const viewAllTab = getSearchParam('viewAllTab');
  useEffect(() => {
    if (oppId) {
      setSelectedSalesOpp({ id: oppId, oppLocation: oppLocation || 'quickViewSalesOpp' });
      setViewAll(true);
      setViewAllType('salesOpportunityCardFromQV');
    } else if (viewAllTab) {
      setViewAll(true);
      setViewAllType(viewAllTab);
    } else {
      setViewAll(false);
      setViewAllType('');
    }
  }, [oppId, oppLocation, viewAllTab]);

  // clean params if clientId is null
  useEffect(() => {
    if (!getSearchParam('client')) {
      setValue('clientTab', 'quick-view');
      setValue('collectionId', '');
    }
  }, []);

  // Exit view all if new client selected
  useEffect(() => {
    setViewAll(false);
    setViewAllType('');
    setValue('viewAllTab', '');
  }, [client?.id]);

  // Auto-open chat if openChat param is present
  const openChatParam = getSearchParam('openChat');
  useEffect(() => {
    if (openChatParam === 'true' && client?.id) {
      // Small delay to ensure client data is loaded
      const timer = setTimeout(() => {
        showMessageHandler();
        // Clear the param so it doesn't re-open on refresh
        setValue('openChat', '');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [client?.id, openChatParam]);

  const handleChangeTab = key => {
    setValue('clientTab', key);
    setValue('collectionId', '');
  };

  return client.id ? (
    <div>
      {viewAll ? (
        <SelectViewAll
          allType={viewAllType}
          handleViewAll={handleViewAll}
          allAssoc={allAssoc}
          client={client}
          auth={auth}
          isFetchingAutoMessages={isFetchingAutoMessages}
          refetchAutoMessages={refetchAutoMessages}
          setSelectedSalesOpp={setSelectedSalesOpp}
          setDismissPopup={setDismissPopup}
          dismissPopup={dismissPopup}
          associate={associate}
          setAssociate={setAssociate}
          showPopConfirm={showPopConfirm}
          closeChat={closeChat}
          showMessageHandler={showMessageHandler}
          selectedSalesOpp={selectedSalesOpp}
        />
      ) : (
        <>
          <ClientHeader
            setAssignPopup={setAssignPopup}
            setDrawerToggle={setDrawerToggle}
            toggleDrawer={toggleDrawer}
            chatIconColor={chatIconColor}
            type={type}
            showMessageHandler={showMessageHandler}
            closeChat={closeChat}
            showPopConfirm={showPopConfirm}
            setShowPopConfirm={setShowPopConfirm}
          />
          <div
            className="flex-col-left p-btm-7 cd-tabs"
            style={{
              flex: 1,
              overflow: 'hidden',
              alignItems: 'stretch',
              maxHeight: `calc(100vh - ${paymentBanner ? '215' : '150'}px)`,
            }}
          >
            <Tabs
              centered
              size="large"
              tabBarStyle={{ marginTop: '20px' }}
              type="card"
              items={tabList}
              style={{ maxHeight: `calc(100vh - ${paymentBanner ? '220' : '155'}px)` }}
              defaultActiveKey="quick-view"
              onChange={handleChangeTab}
              activeKey={clientTabParams || 'quick-view'}
            />
          </div>
        </>
      )}
    </div>
  ) : (
    <div />
  );
};

export default ClientsV2Details;
